import React from 'react';

const styles = {
  container: {
    marginBottom: '20px',
  },
  chartContainer: {
    marginTop: '10px',
  },
  barRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '8px',
  },
  categoryLabel: {
    width: '25%',
    fontSize: '10px',
    paddingRight: '10px',
    fontFamily: 'Poppins, sans-serif',
  },
  barContainer: {
    width: '75%',
    height: '20px',
    display: 'flex',
    flexDirection: 'row',
  },
  // Base styles
  baseText: {
    fontFamily: 'Poppins, sans-serif',
  },
  explanationText: {
    fontSize: '12px',
    color: '#666666',
    lineHeight: '1.4',
    marginBottom: '20px',
    fontFamily: 'Poppins, sans-serif',
  },
  // Phase bar colors
  phaseBar1: { backgroundColor: '#FF6B6B' }, // Bright Coral
  phaseBar2: { backgroundColor: '#4ECDC4' }, // Medium Turquoise
  phaseBar3: { backgroundColor: '#6C5CE7' }, // Soft Purple
  phaseBar4: { backgroundColor: '#A8E6CF' }, // Mint Green
  phaseBar5: { backgroundColor: '#FFD93D' }, // Bright Yellow
  phaseBar6: { backgroundColor: '#FF8B94' }, // Soft Pink
  // Legend
  legend: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    marginTop: '15px',
  },
  legendItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: '15px',
    marginBottom: '5px',
    width: '30%',
  },
  legendColor: {
    width: '12px',
    height: '12px',
    marginRight: '5px',
  },
  legendText: {
    fontSize: '10px',
    fontFamily: 'Poppins, sans-serif',
  },
  // Details section
  detailsSection: {
    marginTop: '20px',
    borderTop: '1px solid #e0e0e0',
    paddingTop: '10px',
  },
  categoryDetails: {
    marginBottom: '15px',
    backgroundColor: '#f5f5f5',
    padding: '10px',
    borderRadius: '4px',
  },
  categoryTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#333333',
    fontFamily: 'Poppins, sans-serif',
  },
  phaseDetails: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#ffffff',
    padding: '8px',
    borderRadius: '3px',
    marginTop: '5px',
  },
  phaseItem: {
    width: '33%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '3px',
    paddingRight: '5px',
  },
  phaseCount: {
    fontSize: '10px',
    color: '#666666',
    fontFamily: 'Poppins, sans-serif',
  },
  totalCount: {
    fontSize: '10px',
    color: '#444444',
    marginBottom: '5px',
    paddingLeft: '2px',
    fontFamily: 'Poppins, sans-serif',
  }
};

const PHASES = {
  EMPATHIZE: 'Empathize',
  DEFINE: 'Define',
  IDEATE: 'Ideate',
  PROTOTYPE: 'Prototype',
  TEST: 'Test',
  COMMUNICATE: 'Communicate'
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const BreakdownWeb = ({ data }) => {
  if (!data) return null;

  const categories = Object.entries(data).map(([category, phases]) => ({
    name: capitalizeFirstLetter(category),
    phases: [
      phases[PHASES.EMPATHIZE] || 0,
      phases[PHASES.DEFINE] || 0,
      phases[PHASES.IDEATE] || 0,
      phases[PHASES.PROTOTYPE] || 0,
      phases[PHASES.TEST] || 0,
      phases[PHASES.COMMUNICATE] || 0
    ]
  }));

  // Calculate total competencies and phase counts for each category
  const categoryDetails = Object.entries(data).map(([category, phaseData]) => {
    const { total, ...phases } = phaseData;
    
    // Calculate actual count for each phase based on percentage
    const phaseValues = Object.entries(phases).map(([phase, percentage]) => ({
      phase,
      percentage,
      count: Math.round((percentage / 100) * total)
    }));

    return {
      category: capitalizeFirstLetter(category),
      phases: phaseValues,
      total
    };
  });

  const getPhaseBarStyle = (index) => {
    switch (index) {
      case 0: return styles.phaseBar1;
      case 1: return styles.phaseBar2;
      case 2: return styles.phaseBar3;
      case 3: return styles.phaseBar4;
      case 4: return styles.phaseBar5;
      case 5: return styles.phaseBar6;
      default: return styles.phaseBar1;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.explanationText}>
        Shows competency distribution across design thinking phases for each category. Each bar represents a category, colored segments show the percentage of competencies in each phase.
        <br/><br/>
        <strong>How percentages are calculated:</strong> For each category, percentages show what portion of competencies in that category belong to each phase. For example, if Skills shows 60% Empathize, it means 60% of all selected Skills competencies are in the Empathize phase. Percentages within each category always add up to 100%.
      </div>
      <div style={styles.chartContainer}>
        {categories.map((category, index) => {
          const hasCompetencies = category.phases.some(phase => phase > 0);
          
          return (
            <div key={index} style={styles.barRow}>
              <div style={styles.categoryLabel}>{category.name}</div>
              <div style={styles.barContainer}>
                {hasCompetencies ? (
                  category.phases.map((phase, phaseIndex) => (
                    <div
                      key={phaseIndex}
                      style={{
                        ...getPhaseBarStyle(phaseIndex),
                        width: `${phase}%`,
                        height: '100%'
                      }}
                    />
                  ))
                ) : (
                  <div
                    style={{
                      backgroundColor: '#e0e0e0',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.legend}>
        {Object.values(PHASES).map((phase, index) => (
          <div key={index} style={styles.legendItem}>
            <div style={{...styles.legendColor, ...getPhaseBarStyle(index)}} />
            <div style={styles.legendText}>{phase}</div>
          </div>
        ))}
      </div>

      <div style={styles.detailsSection}>
        {categoryDetails.map((category, index) => (
          <div key={index} style={styles.categoryDetails}>
            <div style={styles.categoryTitle}>{category.category}</div>
            <div style={styles.totalCount}>
              Total: {category.total} competencies
            </div>
            <div style={styles.phaseDetails}>
              {category.phases.map((phase, phaseIndex) => (
                phase.percentage > 0 && (
                  <div key={phaseIndex} style={styles.phaseItem}>
                    <div 
                      style={{
                        ...styles.legendColor,
                        ...getPhaseBarStyle(phaseIndex)
                      }} 
                    />
                    <div style={styles.phaseCount}>
                      {phase.phase}: {phase.percentage}% ({phase.count})
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BreakdownWeb;
